// @ts-ignore
import { FormAccordionSummary } from 'components/form';
import { openInNewTab } from 'functions/misc';
import postalCodes from 'norway-postal-codes';
import { useNavigate } from 'react-router';

import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import {
  Accordion,
  AccordionDetails,
  Box,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import { useTranslation } from 'react-i18next';
import CustomAvatar from './customAvatar';

function CompanyView(props: any) {
  const { t } = useTranslation();

  /* INITIALIZATIONS */
  let navigate = useNavigate();
  const { company } = props;
  /* FUNCTIONS */
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
      }}
    >
      {/* HEADING */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Box sx={{ pt: 1 }}>
            <FeedRoundedIcon sx={{ fontSize: 40 }} />
          </Box>
          <Box sx={{ pl: 2, pt: 2, flexGrow: 1 }}>
            <Typography
              sx={{
                textTransform: 'capitalize',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 'bold',
                fontSize: 18,
                letterSpacing: 4,
              }}
            >
              {t('companyView.header')}
            </Typography>
          </Box>
          {company.id !== 'new' && company.name > '' && (
            <Box>
              <IconButton
                sx={{ mt: 0 }}
                onClick={(e) => {
                  openInNewTab(
                    e,
                    'https://www.google.com/maps/place/' +
                      company.mainAddress?.street +
                      ',' +
                      company.mainAddress?.postalCode +
                      ',' +
                      company.mainAddress?.city +
                      '/'
                  );
                }}
              >
                <TravelExploreRoundedIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <IconButton
                sx={{ mt: -1, mb: -1 }}
                onClick={() => {
                  navigate(`/companies/${company.id}`);
                }}
              >
                <LaunchRoundedIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>

      <Stack direction='row'>
        <Grid
          item
          sx={{
            width: 384,
            height: 192,
            mt: 3,
            mb: 2,
            borderRadius: 3,
            backgroundColor: 'primary.light',
            display: 'flex',
          }}
        >
          {company?.url ? (
            <Box>
              <img
                src={company?.url || MissingPhoto.COMPANY_URL}
                style={{
                  padding: 0,
                  width: 384,
                  height: 192,
                  borderRadius: 3,
                  objectFit: 'scale-down',
                }}
                alt={'previewUrl'}
              />
            </Box>
          ) : (
            <Box>
              <CustomAvatar
                avatarName={company?.name || ''}
                imgHeight={192}
                imgWidth={384}
              />
            </Box>
          )}
        </Grid>
        <Grid
          container
          sx={{
            p: 2,
          }}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
          <Grid item xs={12} lg={8}>
            <TextField
              id='name'
              label={t('companyView.name')}
              variant='standard'
              value={company?.name}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <TextField
              id='orgnr'
              label={t('companyView.orgNumber')}
              variant='standard'
              value={company?.organizationNumber}
            />
          </Grid>

          <Grid item xs={12} lg={8}>
            <TextField
              id='email'
              label={t('companyView.email')}
              variant='standard'
              value={company?.email}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <TextField
              id='phone'
              label={t('companyView.phone')}
              variant='standard'
              value={company?.phone}
            />
          </Grid>

          <Grid item xs={12} lg={8}>
            <TextField
              id='webPage'
              label={t('companyView.website')}
              variant='standard'
              value={company?.webPage}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <TextField
              id='companyType'
              label={t('companyView.type')}
              variant='standard'
              value={company?.companyType}
            />
          </Grid>
        </Grid>
      </Stack>

      {/*     Main Address */}
      <Grid
        item
        xs={12}
        sx={{
          mb: 2,
          border: 0,
          borderRadius: 2,
          boxShadow:
            '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
        }}
      >
        <Accordion
          disableGutters
          sx={{
            backgroundColor: 'transparent',
            border: 0,
          }}
        >
          <FormAccordionSummary
            Icon={HomeRoundedIcon}
            title={t('address.mainAddress')}
          />
          <AccordionDetails>
            <Grid container>
              <Grid
                item
                xs={6}
                lg={6}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.street'
                  label={t('address.street')}
                  variant='standard'
                  value={company.mainAddress?.street || ' '}
                />
              </Grid>

              <Grid
                item
                xs={2}
                lg={2}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.postalCode'
                  label={t('address.zip')}
                  variant='standard'
                  value={company.mainAddress?.postalCode || ' '}
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.postalArea'
                  label={t('address.area')}
                  variant='standard'
                  value={
                    postalCodes[`${company.mainAddress?.postalCode}`] || ' '
                  }
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.city'
                  label={t('address.city')}
                  variant='standard'
                  value={company.mainAddress?.city || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.country'
                  label={t('address.country')}
                  variant='standard'
                  value={company.mainAddress?.country || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='mainAddress.postbox'
                  label={t('address.postbox')}
                  variant='standard'
                  value={company.mainAddress?.postBox || ' '}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/*     Invoice Address */}
      <Grid
        item
        xs={12}
        sx={{
          mb: 2,
          border: 0,
          borderRadius: 2,
          boxShadow:
            '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
        }}
      >
        <Accordion
          disableGutters
          sx={{
            backgroundColor: 'transparent',
            border: 0,
          }}
        >
          <FormAccordionSummary
            Icon={HomeRoundedIcon}
            title={t('address.deliveryAddress')}
          />
          <AccordionDetails>
            <Grid container>
              <Grid
                item
                xs={6}
                lg={6}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.street'
                  label={t('address.street')}
                  variant='standard'
                  value={company.deliveryAddress?.street || ' '}
                />
              </Grid>

              <Grid
                item
                xs={2}
                lg={2}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.postalCode'
                  label={t('address.zip')}
                  variant='standard'
                  value={company.deliveryAddress?.postalCode || ' '}
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.postalArea'
                  label={t('address.area')}
                  variant='standard'
                  value={
                    postalCodes[`${company.deliveryAddress?.postalCode}`] || ' '
                  }
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.city'
                  label={t('address.city')}
                  variant='standard'
                  value={company.deliveryAddress?.city || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.country'
                  label={t('address.country')}
                  variant='standard'
                  value={company.deliveryAddress?.country || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='deliveryAddress.postbox'
                  label={t('address.postbox')}
                  variant='standard'
                  value={company.deliveryAddress?.postBox || ' '}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/*     Delivery Address */}
      <Grid
        item
        xs={12}
        sx={{
          mb: 2,
          border: 0,
          borderRadius: 2,
          boxShadow:
            '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
        }}
      >
        <Accordion
          disableGutters
          sx={{
            backgroundColor: 'transparent',
            border: 0,
          }}
        >
          <FormAccordionSummary
            Icon={HomeRoundedIcon}
            title={t('address.billingAddress')}
          />
          <AccordionDetails>
            <Grid container>
              <Grid
                item
                xs={6}
                lg={6}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.street'
                  label={t('address.street')}
                  variant='standard'
                  value={company.invoiceAddress?.street || ' '}
                />
              </Grid>

              <Grid
                item
                xs={2}
                lg={2}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.postalCode'
                  label={t('address.zip')}
                  variant='standard'
                  value={company.invoiceAddress?.postalCode || ' '}
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.postalArea'
                  label={t('address.area')}
                  variant='standard'
                  value={
                    postalCodes[`${company.invoiceAddress?.postalCode}`] || ' '
                  }
                />
              </Grid>

              <Grid
                item
                xs={4}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.city'
                  label={t('address.city')}
                  variant='standard'
                  value={company.invoiceAddress?.city || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.country'
                  label={t('address.country')}
                  variant='standard'
                  value={company.invoiceAddress?.country || ' '}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  pl: 1,
                  pr: 1,
                }}
              >
                <TextField
                  id='invoiceAddress.postbox'
                  label={t('address.postbox')}
                  variant='standard'
                  value={company.invoiceAddress?.postBox || ' '}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
}

export default CompanyView;
